import { createContext } from 'react';

import { type getTeams } from '@/tsClient/my';
import type * as ApiModels from '@/typings/api-models';

import { type Team, type TeamOwner } from './constants';

type TeamsContextType = {
  hasFetchedTeams: boolean;
  setCurrentTeam: (newTeam: Team | null) => void;
  creatorId: string | null;
  currentTeam: Team | null;
  currentTeamOwner: TeamOwner | null;
  refetchTeams: () => Promise<Awaited<ReturnType<typeof getTeams>>['items']>;
  teams: Team[];
  role: Omit<ApiModels.TeamMemberRoles, 'id' | 'teamMemberId'>;
  workflowRoles: ApiModels.WorkflowRole[];
  setWorkflowRoles: (workflowRoles: ApiModels.WorkflowRole[]) => void;
  fetchWorkflowRoles: () => Promise<void>;
};

/**
 * I at least am confused by what is provided by this context.
 * creatorId - The creator.id of the selected team
 * currentTeamOwner - The currently selected creator
 * currentTeam - The current users TeamMember for the selected creator. Ie { id: teamMemberId, role: Role, teamOwner: creator }
 *
 * So creatorId === currentTeamOwner.id === currentTeam.teamOwner.id
 */

const TeamsContext = createContext<TeamsContextType>({
  creatorId: null,
  currentTeam: null,
  currentTeamOwner: null,
  fetchWorkflowRoles: async () => {},
  hasFetchedTeams: false,
  refetchTeams: async () => [],
  role: {
    admin: false,
    finance: false,
    manageTeam: false,
    role: 'caseWorker',
  },
  setCurrentTeam: () => undefined,
  setWorkflowRoles: () => undefined,
  teams: [],
  workflowRoles: [],
});

export default TeamsContext;
